import { Box, type BoxProps, Button, HStack, Icon } from '@chakra-ui/react';
import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { FiBold, FiCheck, FiEdit2, FiItalic, FiLink, FiUnderline } from 'react-icons/fi';
import { LuCode, LuHeading1, LuHeading2, LuHeading3, LuHeading4, LuList, LuListOrdered, LuQuote, LuStrikethrough } from 'react-icons/lu';
import { type ComponentType, createContext, type ReactNode, useContext } from 'react';

const RichTextEditorContext = createContext<Editor | null>(null);

export const useRichTextEditorContext = () => useContext(RichTextEditorContext);

type RootProps = BoxProps & {
  editor: Editor | null;
  children: ReactNode;
};

const Root = ({ editor, children, ...props }: RootProps) => (
  <RichTextEditorContext.Provider value={editor}>
    <Box
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      overflow="hidden"
      bg="bg.panel"
      shadow="sm"
      {...props}
    >
      {children}
    </Box>
  </RichTextEditorContext.Provider>
);

type ToolbarProps = React.ComponentProps<typeof HStack> & {
  children: ReactNode;
};

const Toolbar = ({ children, ...props }: ToolbarProps) => (
  <HStack
    gap={2}
    p={2}
    borderBottomWidth="1px"
    borderColor="border"
    bg="bg.subtle"
    flexWrap="wrap"
    {...props}
  >
    {children}
  </HStack>
);

const ControlGroup = ({ children, ...props }: React.ComponentProps<typeof HStack> & { children: ReactNode }) => (
  <HStack gap={1.5} {...props}>
    {children}
  </HStack>
);

const Content = ({ ...props }: BoxProps) => {
  const editor = useRichTextEditorContext();

  return (
    <Box
      minH="280px"
      bg="white"
      _focusWithin={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px var(--chakra-colors-orange-500)' }}
      {...props}
    >
      <EditorContent editor={editor} />
    </Box>
  );
};

type ControlButtonProps = {
  icon?: ComponentType<{ size?: number | string }>;
  label?: string;
  active?: boolean;
  onClick: () => void;
};

const ControlButton = ({ icon, label, active, onClick }: ControlButtonProps) => (
  <Button
    type="button"
    size="xs"
    variant={active ? 'solid' : 'ghost'}
    color={active ? 'white' : 'black'}
    bg={active ? 'black' : 'transparent'}
    _hover={{ bg: active ? 'black' : 'gray.100' }}
    borderRadius="sm"
    minW="2.25rem"
    px={2}
    onClick={onClick}
  >
    {icon ? <Icon as={icon} boxSize={3.5} /> : label}
  </Button>
);

const createControl = (
  icon: ComponentType<{ size?: number | string }>,
  isActive: (editor: Editor) => boolean,
  run: (editor: Editor) => void,
) => {
  const Component = () => {
    const editor = useRichTextEditorContext();

    return (
      <ControlButton
        icon={icon}
        active={Boolean(editor && isActive(editor))}
        onClick={() => {
          if (editor) {
            run(editor);
          }
        }}
      />
    );
  };

  return Component;
};

const createLinkControl = () => {
  const Component = () => {
    const editor = useRichTextEditorContext();

    return (
      <Button
        type="button"
        size="xs"
        variant={editor?.isActive('link') ? 'solid' : 'ghost'}
        color={editor?.isActive('link') ? 'white' : 'black'}
        bg={editor?.isActive('link') ? 'black' : 'transparent'}
        _hover={{ bg: editor?.isActive('link') ? 'black' : 'gray.100' }}
        borderRadius="sm"
        minW="2.25rem"
        px={2}
        onClick={() => {
          const nextValue = window.prompt('URL:', editor?.getAttributes('link')?.href ?? 'https://');
          if (!nextValue || !editor || nextValue === 'https://') {
            return;
          }
          editor.chain().focus().extendMarkRange('link').setLink({ href: nextValue }).run();
        }}
      >
        <Icon as={FiLink} boxSize={3.5} />
      </Button>
    );
  };

  return Component;
};

export const Control = {
  Bold: createControl(FiBold, (editor) => editor.isActive('bold'), (editor) => editor.chain().focus().toggleBold().run()),
  Italic: createControl(FiItalic, (editor) => editor.isActive('italic'), (editor) => editor.chain().focus().toggleItalic().run()),
  Underline: createControl(FiUnderline, (editor) => editor.isActive('underline'), (editor) => editor.chain().focus().toggleUnderline().run()),
  Strike: createControl(LuStrikethrough, (editor) => editor.isActive('strike'), (editor) => editor.chain().focus().toggleStrike().run()),
  BulletList: createControl(LuList, (editor) => editor.isActive('bulletList'), (editor) => editor.chain().focus().toggleBulletList().run()),
  OrderedList: createControl(LuListOrdered, (editor) => editor.isActive('orderedList'), (editor) => editor.chain().focus().toggleOrderedList().run()),
  Blockquote: createControl(LuQuote, (editor) => editor.isActive('blockquote'), (editor) => editor.chain().focus().toggleBlockquote().run()),
  H1: createControl(LuHeading1, (editor) => editor.isActive('heading', { level: 1 }), (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run()),
  H2: createControl(LuHeading2, (editor) => editor.isActive('heading', { level: 2 }), (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run()),
  H3: createControl(LuHeading3, (editor) => editor.isActive('heading', { level: 3 }), (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run()),
  H4: createControl(LuHeading4, (editor) => editor.isActive('heading', { level: 4 }), (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run()),
  Code: createControl(LuCode, (editor) => editor.isActive('codeBlock') || editor.isActive('code'), (editor) => editor.chain().focus().toggleCodeBlock().run()),
  Undo: createControl(FiEdit2, () => false, (editor) => editor.chain().focus().undo().run()),
  Redo: createControl(FiCheck, () => false, (editor) => editor.chain().focus().redo().run()),
  Link: createLinkControl(),
};

export const RichTextEditor = {
  Root,
  Toolbar,
  ControlGroup,
  Content,
};
